'use client';

import heroBackground from '@/public/home/heroBackground.png';
import Image from 'next/image';
import { useRef } from 'react';
import { easeIn, easeInOut, easeOut, motion, useScroll, useTransform } from 'framer-motion';
import { PortableTextBlock } from 'sanity';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton } from '@/sanity/lib/definitions';
import TrustChip from '@/components/global/TrustChip';
import { CTAButton } from '@/shadcn/ui/button';
import ChipText from '@/components/global/ChipText';
import heroCard from '@/public/home/heroCard.png';
import manInTheOffice from '@/public/home/challengeImage.png';
import challengeImageMobile from '@/public/home/challengeImageMobile.png';
import greenShape from '@/public/home/greenShape.svg';

interface IChallengeProps {
  items: {
    text: string;
    description: string;
    number: string;
  }[];
  chipText: string;
  title: PortableTextBlock[];
  buttonOne: GROQButton;
  bigText: PortableTextBlock[];
  buttonTwo: GROQButton;
}

export default function Challenge({
  items,
  chipText,
  title,
  buttonOne,
  bigText,
  buttonTwo,
}: IChallengeProps) {
  const isMobile = useIsMobile();

  return (
    <section className="w-full flex flex-col items-center pt-[8.61rem] lg:pt-[9.63rem] pb-[13rem] lg:pb-[16.44rem]">
      <div className="container flex flex-col gap-[12.96rem] lg:gap-[17.12rem] items-center justify-center w-full relative">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-[2rem] lg:gap-0 w-full">
          {items?.length > 0 && items?.map((item, index) => (
            <div key={index} className="flex flex-row lg:flex-col items-center lg:gap-[2rem] w-full lg:max-w-[25rem] mx-auto border-b border-[#011410]/[16%] lg:border-b-0 last:border-b-0 pb-[2rem] lg:pb-0">
              <p className="text-[1.75rem] font-[400] text-[#072922]/40 lg:w-full flex-shrink-0 mb-0 pr-[8rem]">0{index + 1}</p>
              <div className="flex flex-col gap-[.5rem] w-full">
                <p className="text-[1.75rem] font-[400] text-[#072922]/80 leading-[120%] mb-0">{item.text}</p>
                <p className="text-[1.75rem] font-[400] text-[#072922]/40 leading-[120%] mb-0">{item.description}</p>
              </div>
              <p className="text-[6rem] font-[400] text-[#072922] lg:w-full mb-0 leading-[120%] flex-shrink-0">{item.number}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-[3rem] items-center min-h-[67.375rem]">
          <div className="flex flex-col gap-[2rem] lg:gap-[2rem] prose-headings:leading-[120%] prose-headings:mb-[1rem] lg:prose-headings:mb-[3rem] mb-[10.54rem] lg:mb-0">
            {chipText &&
              <ChipText text={chipText} color="purple" />
            }
            <PortableText value={title} components={richTextComponent} />
            <CTAButton className="mt-[2rem] lg:mt-[3rem] lg:w-fit" data={buttonOne} />
          </div>
          <Image
            src={manInTheOffice}
            alt="Man in the office"
            className="w-full h-auto hidden lg:block"
            quality={100}
          />
          <Image
            src={challengeImageMobile}
            alt="Man in the office"
            className="w-full h-auto lg:hidden"
            quality={100}
          />
          <div className="flex flex-col min-h-[46.25rem] lg:min-h-auto h-full justify-between relative p-[3rem] lg:p-[4.25rem] lg:pb-[3.81rem] mt-[2rem] lg:mt-0 bg-[#B0F4B7] lg:bg-transparent">
            <div className="relative prose-headings:text-[4.5rem] lg:prose-headings:text-[7rem] prose-headings:mb-[1.5rem] lg:prose-headings:mb-[2rem] z-10">
              <PortableText value={bigText} components={richTextComponent} />
            </div>
            <CTAButton className="w-fit min-w-[23.75rem] lg:min-w-auto" data={buttonTwo} />

            <svg className="absolute scale-[.82] bottom-[-4.5rem] right-[-16rem] z-[1] -scale-y-100 lg:hidden" width="243" height="106" viewBox="0 0 243 106" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M242.08 105.999V0H0C2.20914 0 4.00003 1.79086 4.00003 4V98C4.00003 100.209 5.79103 102 8.00006 102H238.001C240.209 102 242 103.79 242.001 105.999H242.08Z" fill="#F6F4E9"/>
            </svg>

            <svg className="absolute z-0 inset-0 hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 539 669" fill="none">
              <path d="M539 551.31C539 553.519 537.209 555.31 535 555.31L422.405 555.31C420.196 555.31 418.405 557.101 418.405 559.31L418.405 664.5C418.405 666.709 416.615 668.5 414.405 668.5L218.844 668.5L4.00101 668.5C1.79184 668.5 0.00100845 666.709 0.00100855 664.5L-6.121e-05 3.99997C-6.11134e-05 1.79083 1.79083 -2.34822e-05 3.99994 -2.33856e-05L535 -1.74845e-07C537.209 -7.82807e-08 539 1.79086 539 4L539 551.31Z" fill="#B0F4B7"/>
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}
